import {useCallback, useEffect, useState} from 'react';

import {useSelector} from 'react-redux';

import {deleteUserCreditCard, fetchUserPaymentMethods} from '~/shared/services/bffService/api';
import {ApiError, is401Error, isApiError} from '~/shared/services/apiErrorService';
import {handleRefreshToken} from '~/shared/services/auth';
import {MoneyCard, UserGetPaymentMethodsResponse} from '~/shared/services/bffService/api/interfaces/responses';
import {UserCreditCard, UserPaypal} from '~/personalArea/components/Sections/Payments/types';
import {trackEvent} from '~/shared/services/analytics';
import {useLocation} from '~/shared/router';
import apiService from '~/shared/services/apiService';
import {selectUser} from '~/shared/store/storeModules/user';
import {convertPathnameToAnalyticsPageSource} from '~/shared/services/analytics/utils';
import {createLogger} from '~/shared/logging';

const logger = createLogger('usePayments');

const combinePaymentsInList = (paymentsData: UserGetPaymentMethodsResponse): (UserCreditCard | UserPaypal)[] => {
  const cards: UserCreditCard[] = paymentsData.creditCards || [];
  const paypals: UserPaypal[] = paymentsData.paypal.map(p => ({...p, type: 'paypal'})) || [];

  return [...cards, ...paypals];
};

const usePayments = ({
  autoFetch = true,
}: {
  autoFetch?: boolean;
} = {}) => {
  const [payments, setPayments] = useState<(UserCreditCard | UserPaypal)[] | null>(null);
  const [moneyCards, setMoneyCards] = useState<MoneyCard[] | null>(null);
  const [isRequestLoading, setIsRequestLoading] = useState(false);
  const [isRequestLoaded, setIsRequestLoaded] = useState(false);
  const [arePaymentsSuccessfullyFetched, setArePaymentsSuccessfullyFetched] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const location = useLocation();
  const user = useSelector(selectUser);

  const getUserPayments = useCallback(async () => {
    setIsRequestLoaded(false);
    setIsRequestLoading(true);
    try {
      const paymentsData = await fetchUserPaymentMethods();

      setPayments(combinePaymentsInList(paymentsData));
      setMoneyCards(paymentsData.moneyCards);
      setArePaymentsSuccessfullyFetched(true);
      setIsRequestLoaded(true);
    } catch (err) {
      if (is401Error(err)) {
        await handleRefreshToken(err, getUserPayments);
        return;
      }
      if (isApiError(err)) {
        setError(err);
      }
      if (!isApiError(err)) {
        logger.warn('Unexpected error in getPayments');
      }
    } finally {
      setIsRequestLoading(false);
    }
  }, []);

  const removePaymentMethodHandler = useCallback(async (args: {
    id: number;
    type: string;
  }): Promise<boolean> => {
    try {
      const {id, type} = args;
      if (type !== 'paypal') {
        await deleteUserCreditCard(id);
      }
      if (type === 'paypal') {
        await apiService.removePayPalUserToken({paypalId: id});
      }
      trackEvent('removePaymentMethod_successful', {
        linkType: type === 'paypal' ? type : 'credit_card',
        pageSource: convertPathnameToAnalyticsPageSource(location.pathname),
      });
      getUserPayments();
      return true;
    } catch (err) {
      if (is401Error(err)) {
        return handleRefreshToken(err, removePaymentMethodHandler, args);
      }

      return false;
    }
  }, [getUserPayments, location.pathname]);

  useEffect(() => {
    if (autoFetch && !arePaymentsSuccessfullyFetched && user.loaded && !user.loading) {
      getUserPayments();
    }
  }, [getUserPayments, arePaymentsSuccessfullyFetched, user, autoFetch]);

  const tenbisCreditBalance = moneyCards?.find(card => card.isTenbisCredit)?.balance || 0;

  const activateMoneyCardById = useCallback((id: string) => {
    if (!moneyCards?.length) {
      logger.warn('No moneycards to activate');
      return;
    }

    setMoneyCards(prevState => (prevState ?
      prevState.map(mc => (mc.id === id ? {...mc, isActivated: true} : mc)) :
      prevState
    ));
  }, [moneyCards]);

  return {
    payments,
    moneyCards,
    tenbisCreditBalance,
    isLoaded: isRequestLoaded && user.loaded && !user.loading,
    isLoading: isRequestLoading,
    activateMoneyCardById,
    removePaymentMethodHandler,
    getUserPayments,
    error,
  };
};

export default usePayments;
